import { type ColumnDef } from '@tanstack/react-table';
import type { TFunction } from 'i18next';
import { Badge } from '@/shared/components/ui/badge';
import { useUIStore } from '@/core/stores/ui.store';
import { formatInstant } from '@/shared/lib/datetime/instant';
import { formatTrackedHours, type TimeEntryDto } from '@/features/time-tracking/public';

function moment(iso: string | null): string {
  const { dateFormat, timeFormat, timezone } = useUIStore.getState();
  return formatInstant(iso, { dateFormat, timeFormat, timezone, showTime: true });
}

export function getTimeEntryColumns(
  t: TFunction,
  onTaskClick?: (taskId: string) => void,
): ColumnDef<TimeEntryDto, unknown>[] {
  return [
    {
      accessorKey: 'task',
      header: t('table.columns.task'),
      meta: { cellClassName: 'flex flex-[2] min-w-0' },
      cell: ({ row }) => {
        const taskId = row.original.task?.id;
        const title = row.original.task?.title ?? '—';
        if (taskId && onTaskClick) {
          return (
            <button
              type="button"
              className="truncate font-medium text-foreground hover:underline"
              onClick={() => onTaskClick(taskId)}
            >
              {title}
            </button>
          );
        }
        return <span className="truncate font-medium text-foreground">{title}</span>;
      },
    },
    {
      accessorKey: 'project',
      header: t('table.columns.project'),
      // cellClassName must include `flex` — the cell wrapper div otherwise stays
      // block-level and a plain inline <span> ignores `truncate` (text-overflow
      // requires a block container), same fix already applied to start/end below.
      meta: { cellClassName: 'flex min-w-0 flex-1' },
      cell: ({ row }) => (
        <span className="truncate">{row.original.task?.project?.name ?? '—'}</span>
      ),
    },
    {
      accessorKey: 'person',
      header: t('table.columns.person'),
      meta: { cellClassName: 'flex min-w-0 flex-1' },
      cell: ({ row }) => <span className="truncate">{row.original.user?.displayName ?? '—'}</span>,
    },
    {
      accessorKey: 'start',
      header: t('table.columns.start'),
      meta: { cellClassName: 'hidden min-w-0 flex-1 md:flex' },
      cell: ({ row }) => (
        <span className="truncate text-muted-foreground">{moment(row.original.startTime)}</span>
      ),
    },
    {
      accessorKey: 'end',
      header: t('table.columns.end'),
      meta: { cellClassName: 'hidden min-w-0 flex-1 md:flex' },
      cell: ({ row }) => (
        <span className="truncate text-muted-foreground">
          {row.original.endTime ? moment(row.original.endTime) : t('table.inProgress')}
        </span>
      ),
    },
    {
      accessorKey: 'duration',
      header: t('table.columns.duration'),
      meta: { cellClassName: 'flex-none w-24 tabular-nums font-medium' },
      cell: ({ row }) => formatTrackedHours(row.original.durationHours),
    },
    {
      accessorKey: 'billable',
      header: t('table.columns.billable'),
      meta: { cellClassName: 'flex-none w-28' },
      cell: ({ row }) =>
        row.original.isBillable ? (
          <Badge variant="secondary">{t('table.billableYes')}</Badge>
        ) : (
          <span className="text-muted-foreground">{t('table.billableNo')}</span>
        ),
    },
  ];
}
